import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getLookupOptions } from '@/queries/getLookupOptions'
import { SelectBox, Button } from 'devextreme-react'
import { useState } from 'react'
import axios from 'axios'
import { queryOptions } from '@tanstack/react-query'

export const Route = createFileRoute(
  '/_app/$module_id/$entity_type_id/$entity_id/_tabs/attendance/',
)({
  component: RouteComponent,
})

// Custom query for session attendance since it has a specific endpoint
const getSessionAttendanceQueryOptions = (sessionId: string) => {
  return queryOptions({
    queryKey: ['session-attendance', sessionId],
    queryFn: async () => {
      const response = await axios.get(`/sessions/${sessionId}/attendance`)
      return response.data
    },
  })
}

function RouteComponent() {
  const { entity_id } = Route.useParams()
  const queryClient = useQueryClient()

  // Query for attendance records for this session using the specific sessions endpoint
  const { data: attendances, isLoading } = useQuery(
    getSessionAttendanceQueryOptions(entity_id)
  )

  // Lookup options for status dropdowns
  const { data: attendanceStatuses } = useQuery(getLookupOptions({ discriminator: 'AttendanceStatus' }))
  const { data: homeworkStatuses } = useQuery(getLookupOptions({ discriminator: 'HomeworkStatus' }))
  const { data: mentorStatuses } = useQuery(getLookupOptions({ discriminator: 'MentorStatus' }))

  // Create attendance update mutation for the session-specific endpoint  
  const updateAttendanceMutation = useMutation({
    mutationFn: async (attendance: any) => {
      const response = await axios.post(`/sessions/${entity_id}/attendance`, attendance)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['session-attendance', entity_id] })
    }
  })

  // State for tracking updates
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set())

  // Create voucher mutation
  const createVoucherMutation = useMutation({
    mutationFn: async (clientId: string) => {
      const response = await axios.post(`/clients/${clientId}/vouchers`, {})
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['session-attendance', entity_id] })
    }
  })

  const handleStatusUpdate = async (attendance: any, fieldName: string, value: any) => {
    if (!value) return // Don't update if no value selected

    setUpdatingIds(prev => new Set(prev).add(attendance.Id))

    try {
      // Create updated attendance object
      const updatedAttendance = {
        ...attendance,
        [fieldName]: value
      }

      await updateAttendanceMutation.mutateAsync(updatedAttendance)
    } catch (error) {
      console.error('Error updating status:', error)
    } finally {
      setUpdatingIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(attendance.Id)
        return newSet
      })
    }
  }

  const handleCreateVoucher = async (clientId: string) => {
    try {
      await createVoucherMutation.mutateAsync(clientId)
    } catch (error) {
      console.error('Error creating voucher:', error)
    }
  }

  // Quick status setter functions
  const setAllAttendanceStatus = async (statusId: string) => {
    if (!attendances || !statusId) return

    const promises = attendances.map((attendance: any) =>
      handleStatusUpdate(attendance, 'AttendanceStatusId', statusId)
    )
    await Promise.all(promises)
  }

  if (isLoading) {
    return (
      <div className="p-4 flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading attendance...</div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Session Attendance</h1>
            <p className="text-gray-600 mt-1">Attendance records are automatically created for all registered clients</p>
          </div>
        </div>

        {attendances && attendances.length > 0 && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="text-sm font-medium text-blue-900 mb-3">Quick Actions</h3>
            <div className="flex gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-sm text-blue-700">Mark All Present:</span>
                <SelectBox
                  dataSource={attendanceStatuses || []}
                  valueExpr="id"
                  displayExpr="name"
                  placeholder="Select Status"
                  onValueChanged={(e) => setAllAttendanceStatus(e.value)}
                  width={150}
                />
              </div>
            </div>
          </div>
        )}

        {!attendances || attendances.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No attendance records found</h3>
            <p className="text-gray-500 mb-4">Attendance records will automatically appear here for all registered clients when you visit this page.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Attendance Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Homework Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mentor Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Voucher
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {attendances.map((attendance: any, index: number) => {
                    const isUpdating = updatingIds.has(attendance.Id)

                    return (
                      <tr
                        key={attendance.Id}
                        className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${isUpdating ? 'opacity-50' : ''} hover:bg-blue-50 transition-colors`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <Link
                            to="/$module_id/$entity_type_id/$entity_id"
                            params={{
                              module_id: "outreach",
                              entity_type_id: "clients",
                              entity_id: attendance.clientId,
                            }}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          >
                            {attendance.firstName && attendance.lastName
                              ? `${attendance.firstName} ${attendance.lastName}`.trim()
                              : attendance.firstName || attendance.lastName || 'Unknown Client'}
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <SelectBox
                            dataSource={attendanceStatuses || []}
                            value={attendance.AttendanceStatusId}
                            valueExpr="id"
                            displayExpr="name"
                            placeholder="Select Status"
                            disabled={isUpdating}
                            onValueChanged={(e) =>
                              handleStatusUpdate(attendance, 'AttendanceStatusId', e.value)
                            }
                            className="w-full min-w-[140px]"
                            height={35}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <SelectBox
                            dataSource={homeworkStatuses || []}
                            value={attendance.HomeworkStatusId}
                            valueExpr="id"
                            displayExpr="name"
                            placeholder="Select Status"
                            disabled={isUpdating}
                            onValueChanged={(e) =>
                              handleStatusUpdate(attendance, 'HomeworkStatusId', e.value)
                            }
                            className="w-full min-w-[140px]"
                            height={35}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <SelectBox
                            dataSource={mentorStatuses || []}
                            value={attendance.MentorStatusId}
                            valueExpr="id"
                            displayExpr="name"
                            placeholder="Select Status"
                            disabled={isUpdating}
                            onValueChanged={(e) =>
                              handleStatusUpdate(attendance, 'MentorStatusId', e.value)
                            }
                            className="w-full min-w-[140px]"
                            height={35}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {attendance.VoucherId ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                ✓ Voucher Created
                              </span>
                            ) : (
                              <Button
                                text="Create Voucher"
                                type="normal"
                                onClick={() => handleCreateVoucher(attendance.ClientId)}
                                disabled={!attendance.ClientId || createVoucherMutation.isPending}
                                height={32}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
